import React, { useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Box,
	Button,
	Typography,
	Container,
	CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

const PageContainer = styled(Box)(() => ({
	minHeight: "100vh",
	background:
		"linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	position: "relative",
	overflow: "hidden",
}));

const BackgroundPattern = styled(Box)(() => ({
	position: "absolute",
	width: "100%",
	height: "100%",
	opacity: 0.05,
	backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='%235DBEC3' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
}));

const ContentContainer = styled(Container)(() => ({
	position: "relative",
	zIndex: 1,
	textAlign: "center",
	padding: "40px",
}));

const LogoContainer = styled(Box)(() => ({
	marginBottom: "48px",
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
}));

const ArtehLogo = styled(Box)(() => ({
	width: "200px",
	height: "80px",
	backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 80'%3E%3Ctext x='50%25' y='50%25' font-family='Arial, sans-serif' font-size='48' font-weight='bold' fill='%235DBEC3' text-anchor='middle' dominant-baseline='middle'%3EArteh%3C/text%3E%3C/svg%3E")`,
	backgroundSize: "contain",
	backgroundRepeat: "no-repeat",
	backgroundPosition: "center",
}));

const ButtonContainer = styled(Box)(() => ({
	display: "flex",
	gap: "32px",
	justifyContent: "center",
	marginTop: "48px",
	flexWrap: "wrap",
}));

const StyledButton = styled(Button)(({ variant }) => ({
	minWidth: "200px",
	padding: "18px 40px",
	fontSize: "18px",
	fontWeight: 600,
	textTransform: "none",
	borderRadius: "12px",
	transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
	boxShadow:
		variant === "contained"
			? "0 8px 24px rgba(93, 190, 195, 0.25)"
			: "none",
	border: variant === "outlined" ? "2px solid #5DBEC3" : "none",
	"&:hover": {
		transform: "translateY(-2px)",
		boxShadow:
			variant === "contained"
				? "0 12px 32px rgba(93, 190, 195, 0.35)"
				: "0 8px 24px rgba(93, 190, 195, 0.15)",
	},
}));

const LoginButton = styled(StyledButton)(() => ({
	background: "#5DBEC3",
	color: "#ffffff",
	"&:hover": {
		background: "#4BA3A7",
	},
}));

const SignupButton = styled(StyledButton)(() => ({
	background: "transparent",
	color: "#5DBEC3",
	borderColor: "#5DBEC3",
	"&:hover": {
		background: "rgba(93, 190, 195, 0.08)",
		borderColor: "#4BA3A7",
	},
}));

const ClaimButton = styled(StyledButton)(() => ({
	background: "transparent",
	color: "#4A5568",
	border: "2px solid #CBD5E0",
	"&:hover": {
		background: "rgba(93, 190, 195, 0.06)",
		borderColor: "#5DBEC3",
		color: "#5DBEC3",
	},
}));

const LandingPage: React.FC = () => {
	const navigate = useNavigate();
	const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

	// Redirect to dashboard if already authenticated
	useEffect(() => {
		if (isAuthenticated) {
			navigate("/dashboard");
		}
	}, [isAuthenticated, navigate]);

	const handleLogin = () => {
		loginWithRedirect({
			appState: { returnTo: "/dashboard" },
		});
	};

	const handleSignup = () => {
		loginWithRedirect({
			appState: { returnTo: "/dashboard" },
			authorizationParams: {
				screen_hint: "signup",
			},
		});
	};

	const handleClaim = () => {
		navigate("/claim");
	};

	// Show loading spinner while checking authentication
	if (isLoading) {
		return (
			<PageContainer>
				<CircularProgress sx={{ color: "#5DBEC3" }} />
			</PageContainer>
		);
	}

	// Redirect if authenticated
	if (isAuthenticated) {
		return <Navigate to="/dashboard" replace />;
	}

	return (
		<PageContainer>
			<BackgroundPattern />
			<ContentContainer maxWidth="md">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, ease: "easeOut" }}
				>
					<LogoContainer>
						<ArtehLogo />
					</LogoContainer>

					<Typography
						variant="h2"
						sx={{
							fontSize: { xs: "32px", md: "48px" },
							fontWeight: 300,
							color: "#2c3e50",
							marginBottom: "16px",
							lineHeight: 1.2,
						}}
					>
						Welcome to Your
					</Typography>

					<Typography
						variant="h2"
						sx={{
							fontSize: { xs: "36px", md: "52px" },
							fontWeight: 700,
							background:
								"linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)",
							backgroundClip: "text",
							WebkitBackgroundClip: "text",
							WebkitTextFillColor: "transparent",
							marginBottom: "24px",
							lineHeight: 1.2,
						}}
					>
						Carbon Management Platform
					</Typography>

					<Typography
						variant="body1"
						sx={{
							fontSize: "18px",
							color: "#6c757d",
							maxWidth: "600px",
							margin: "0 auto",
							marginBottom: "48px",
							lineHeight: 1.6,
						}}
					>
						Track, analyze, and reduce your organization's carbon
						footprint with our comprehensive sustainability solution
					</Typography>

					<ButtonContainer>
						<motion.div
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
						>
							<LoginButton
								variant="contained"
								onClick={handleLogin}
								size="large"
							>
								Sign In / Login
							</LoginButton>
						</motion.div>

						<motion.div
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
						>
							<SignupButton
								variant="outlined"
								onClick={handleSignup}
								size="large"
							>
								Sign Up / Join Us
							</SignupButton>
						</motion.div>

						<motion.div
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
						>
							<ClaimButton
								variant="outlined"
								onClick={handleClaim}
								size="large"
							>
								Log in from old app
							</ClaimButton>
						</motion.div>
					</ButtonContainer>

					<Box sx={{ marginTop: "80px" }}>
						<Typography
							variant="body2"
							sx={{
								color: "#95a5a6",
								fontSize: "14px",
							}}
						>
							Trusted by leading organizations worldwide
						</Typography>
					</Box>
				</motion.div>
			</ContentContainer>
		</PageContainer>
	);
};

export default LandingPage;
